import React, { useContext, useEffect, useRef, useState } from "react";
import { Header as TableHeadings, ManageSubCategoriesRow } from "../table";
import {
    DataTable,
    DataTableContainer,
    FlexContainer,
    PortalSearchInput,
    PortalSelect,
    PortalTableActions,
    PortalTableControls,
    Toast
} from "src/components";
import { SaveButton,
    AddButtonWrapper
} from "../style";
import { useTableHooks } from "../../hooks/useTableHooks";
import { BodyAndMindContext } from "../../BodyAndMindContext";
import { hideSubcategory, showSubcategory } from "../../api/subCategoryApi";
import {
    DIRECTORY_LEVEL,
    getDirectoryLevel,
    getDirectoryLevelOption,
    getParentSubcategoryUuid,
    sortDirectoriesByHierarchy
} from "../../directoryLevels";
import { getVisibilityOptions } from "../../hooks/getVisibilityLabel";

export const ManageSubCategoriesTable = (props) => {
    const {
        toggleEditSubcategory,
    } = useContext(BodyAndMindContext);
    const { setSubcategoryToUpdate, setPayload, data, contents, companyCategoriesList, onCreateClick } = props;

    const [selectedCategory, setSelectedCategory] = useState("allCategory");
    const [selectedVisibility, setSelectedVisibility] = useState("allVisibility");
    const [searchTerm, setSearchTerm] = useState("");
    const [shownItems, setShownItems] = useState([]);

    // states for draggable table
    const [rows, setRows] = useState(data);
    const [draggingIndex, setDraggingIndex] = useState(null);
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const visibilityOptions = getVisibilityOptions();
    const getDirectContentCount = item =>
        (contents || []).filter(content =>
            (content.subcategoryUuid || content.noticeboardCategoryUuid || content.subcategory?.uuid) === item.uuid
        ).length;

    const getDirectoryAndDescendantUuids = item => {
        const directoryUuids = [item?.uuid].filter(Boolean);
        const childDirectories = (data || []).filter(directory => getParentSubcategoryUuid(directory) === item?.uuid);

        childDirectories.forEach(childDirectory => {
            directoryUuids.push(...getDirectoryAndDescendantUuids(childDirectory));
        });

        return directoryUuids;
    };

    const getContentCount = item => {
        if (getDirectoryLevel(item) !== DIRECTORY_LEVEL.SUBCATEGORY) {
            return getDirectContentCount(item);
        }

        const directoryUuids = getDirectoryAndDescendantUuids(item);
        return (contents || []).filter(content =>
            directoryUuids.includes(content.subcategoryUuid || content.noticeboardCategoryUuid || content.subcategory?.uuid)
        ).length;
    };

    const SortableColumns = [
        item => item.categoryName || "",
        item => getDirectoryLevelOption(getDirectoryLevel(item)).label,
        item => item.body_and_mind_category?.name || "",
        item => String(getContentCount(item)),
        item => String(item.visibleTo || 0),
        "view_edit", // Not an actual column, no effect
        "hide_show" // Not an actual column, no effect
    ];
    const {
        handleMainCheckbox,
        toggleItemSelection,
        isItemSelected,
        handleSortColumn,
    } = useTableHooks(data, SortableColumns);

    const headings = [
        { name: "Name", textAlign: "left" },
        { name: "Type", textAlign: "left" },
        { name: "Main Category", textAlign: "left" },
        { name: "Parent", textAlign: "left" },
        { name: "Category Path", textAlign: "left" },
        { name: "Content", textAlign: "left" },
        { name: "Visibility", textAlign: "left" },
        { name: "View/Edit", textAlign: "left" },
        { name: "Hide/Show", textAlign: "left" }
    ];

    const isASortableColumn = (index) => index > 0;

    const dragItem = useRef(null);
    const dragOverItem = useRef(null);

    const handleDragStart = (index) => {
        dragItem.current = index;
        setDraggingIndex(index);
    };

    const handleDragEnter = (index) => {
        dragOverItem.current = index;
    };

    const handleDragOver = (index) => {
        setHoveredIndex(index);
    };

    const handleDragEnd = () => {
        const fromIndex = dragItem.current;
        const toIndex = dragOverItem.current;
        if (fromIndex === toIndex || toIndex === null) {
            setDraggingIndex(null);
            setHoveredIndex(null);
            return;
        }

        const updatedRows = [...rows];
        const draggedRow = updatedRows.splice(fromIndex, 1)[0];
        updatedRows.splice(toIndex, 0, draggedRow);
        setRows(updatedRows);
        setDraggingIndex(null);
        setHoveredIndex(null);
        dragItem.current = null;
        dragOverItem.current = null;

        setPayload(
            updatedRows.map((item, index) => ({
                ...item,
                order: index,
            }))
        );

        setRows(updatedRows.map((item, index) => ({
            ...item,
            order: index
        })));
    };

    const changeCategoryHandler = (e) => {
        setSelectedCategory(e.target.value);
    };

    const changeVisibilityHandler = (e) => {
        setSelectedVisibility(e.target.value);
    };

    const searchHandler = event => {
        setSearchTerm(event.target.value);
    };

    useEffect(() => {
        setRows(sortDirectoriesByHierarchy(data));
        setShownItems(data.filter(item => item.deleted_at !== null));
    }, [data]);

    useEffect(() => {
        const nextRows = data.filter(item => {
            const matchesCategory = selectedCategory === "allCategory" || item.categoryUuid === selectedCategory;
            const matchesVisibility = selectedVisibility === "allVisibility" || String(item.visibleTo) === selectedVisibility;
            const searchable = [
                item.categoryName,
                item.body_and_mind_category?.name,
                item.parentSubcategory?.categoryName
            ].filter(Boolean).join(" ").toLowerCase();
            const matchesSearch = searchTerm.trim() === "" || searchable.includes(searchTerm.trim().toLowerCase());

            return matchesCategory && matchesVisibility && matchesSearch;
        });
        setRows(sortDirectoriesByHierarchy(nextRows));
    }, [data, selectedCategory, selectedVisibility, searchTerm]);

    const onShow = async (item) => {
        setShownItems(prev => {
            const isAlreadySelected = prev.some(q => q.uuid === item.uuid);
            if (isAlreadySelected) {
                try {
                    showSubcategory(item.uuid);
                } catch (error) {
                    console.log("Error: ", error);
                    Toast.error(error.getApiErrorDetails().message);
                }
                return prev.filter(q => q.uuid !== item.uuid);
            } else {
                try {
                    hideSubcategory(item.uuid);

                } catch (error) {
                    console.log("Error: ", error);
                    Toast.error(error.getApiErrorDetails().message);
                }

                return [...prev, item];
            }
        });
    };

    const isItemShow = (uuid) =>
        shownItems.some(u => u.uuid == uuid);

    return (

        <FlexContainer height="60vh">
            <FlexContainer marginBottom="15px" direction="row" tDirection="row" justifyContent="space-between" alignItems="flex-end">
                <PortalTableControls>
                    <PortalSearchInput
                        value={searchTerm}
                        onChange={searchHandler}
                        placeholder="Search sub-category, topic or sub-topic"
                        type="text"
                        width="360px"
                    />
                    <PortalSelect value={selectedCategory} onChange={changeCategoryHandler} width="220px">
                        <option value={"allCategory"}>All Categories</option>
                        {companyCategoriesList.map(sub => (
                            <option key={sub.uuid} value={sub.uuid}>
                                {sub.name}
                            </option>
                        ))}
                    </PortalSelect>
                    <PortalSelect value={selectedVisibility} onChange={changeVisibilityHandler} width="190px">
                        <option value="allVisibility">All Visibility</option>
                        {visibilityOptions.map(option => (
                            <option key={option.value} value={String(option.value)}>{option.label}</option>
                        ))}
                    </PortalSelect>
                </PortalTableControls>
                <PortalTableActions>
                    <AddButtonWrapper>
                        <SaveButton
                            width="210px"
                            height="39px"
                            color="#FFFFFF"
                            background="006CFF"
                            border="2px solid #171A22"
                            onClick={onCreateClick}
                        >
                            Create New
                        </SaveButton>
                    </AddButtonWrapper>
                </PortalTableActions>
            </FlexContainer>

            <DataTableContainer>
                <DataTable minWidth="940px">
                    <thead>
                        <TableHeadings
                            headings={headings}
                            onSort={handleSortColumn}
                            onMainCheck={handleMainCheckbox}
                            isSortable={isASortableColumn}
                        />
                    </thead>
                    <tbody>
                        <ManageSubCategoriesRow
                            handleDragStart={handleDragStart}
                            handleDragEnter={handleDragEnter}
                            handleDragEnd={handleDragEnd}
                            handleDragOver={handleDragOver}
                            draggingIndex={draggingIndex}
                            hoveredIndex={hoveredIndex}
                            tableData={rows}
                            getContentCount={getContentCount}
                            onToggle={(item) => toggleItemSelection(item)}
                            isSelected={isItemSelected}
                            onShow={onShow}
                            isItemShow={isItemShow}
                            manageSubCategories={{ toggleEditSubcategory, setSubcategoryToUpdate }}
                            categories={companyCategoriesList}
                        />
                    </tbody>
                </DataTable>
            </DataTableContainer>
        </FlexContainer>

    );
};
